import { useState, useRef, useEffect } from "react";

import AddchartIcon from "@mui/icons-material/Addchart";
import CreateIcon from "@mui/icons-material/Create";
import { Stack, Grid, CircularProgress, Button, Alert, AlertTitle, Box, Link } from "@mui/material";
import BalanceItem from "components/elements/BalanceItem";
import Card from "components/elements/Card";
import { CoinsSwap2 } from "components/icons";
import { useAPIDcaBotsBalances } from "hooks/useAPI";
import useCurrencyPairs from "hooks/useCurrencyPairs";
import useExchangesMetadata from "hooks/useExchangesMetadata";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { DcaBotMode, DcaBotRunState, ExchangeType, OrderTriggering, OrderType } from "types/enums";
import { IBalance } from "types/types";

import CreateGridStrategyButton from "./CreateGridStrategyButton";
import ManualOrderModal from "./ManualOrderModal";
import { CenterDiv } from "./styles";
import TradeModal from "./TradeModal";
import IProps from "./types";

const Balance = ({
  id,
  exchangeTitle,
  exchangeLabel,
  exchangeEnum,
  baseCurrency,
  counterCurrency,
  refreshData,
  isBrokerage,
  totalInvested,
  botRunsList,
  isExchangeEnabled,
  functional,
  bidPrice,
  mode,
  hasExitStrategy,
}: IProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isOpenModal, setIsOpenModal] = useState<boolean>(false);
  const [isManualOrderModal, setIsManualOrderModal] = useState<boolean>(false);
  const [canCreateExitStrategy, setCanCreateExitStrategy] = useState<boolean>(false);
  const { data, error, isValidating, mutate } = useAPIDcaBotsBalances(id, isExchangeEnabled && functional);
  const anycoinInterval = useRef<ReturnType<typeof setInterval> | null>(null);
  const { counterCurrDisplayedScale, baseCurrDisplayedScale } = useExchangesMetadata(
    exchangeEnum ?? "",
    `${baseCurrency}/${counterCurrency}`
  );
  const { data: currencyPairs } = useCurrencyPairs();

  const isBybitEuBalanceEmpty
    = exchangeEnum === ExchangeType.BYBITEU
    && data?.baseCurrencyBalance?.available === 0
    && data?.baseCurrencyBalance?.frozen === 0
    && data?.counterCurrencyBalance?.available === 0
    && data?.counterCurrencyBalance?.frozen === 0;

  useEffect(() => {
    if (currencyPairs.length && exchangeEnum && !canCreateExitStrategy) {
      const exchangeMetadata = currencyPairs.find((item) => item.id === exchangeEnum);
      const currencyPairMetadata = exchangeMetadata.data.find((item: any) => item.key === `${baseCurrency}/${counterCurrency}`);

      if (currencyPairMetadata?.enabled_exit_strategies?.length) {
        setCanCreateExitStrategy(true);
      }
    }
  }, [currencyPairs, exchangeEnum, hasExitStrategy]);

  useEffect(() => {
    if (botRunsList && anycoinInterval.current) {
      const lastOrder = botRunsList.dcaBotRuns[0]?.orders[0];
      if (lastOrder?.status === "FILLED" && lastOrder?.triggering === "INSTANT") {
        handleClose();
        mutateData();
      } else startAnycoinInterval();
    }
  }, [botRunsList]);

  const mutateData = () => {
    mutate();
    refreshData();
  };

  const handleInstantTrade = () => {
    mutateData();

    if (exchangeEnum === ExchangeType.ANYCOIN && !anycoinInterval.current) {
      startAnycoinInterval();
    }
  };

  const handleClose = () => {
    setIsOpenModal(false);
    clearAnycoinInterval();
  };

  const startAnycoinInterval = () => {
    clearAnycoinInterval();
    anycoinInterval.current = setTimeout(mutateData, 2500);
  };

  const clearAnycoinInterval = () => {
    if (anycoinInterval.current) {
      clearTimeout(anycoinInterval.current);
      anycoinInterval.current = null;
    }
  };

  const getCounterCurrencyAmount = (value: number) => {
    if (!bidPrice) return;

    const amount = value * bidPrice;
    return amount ? { value: amount, currency: counterCurrency, scale: counterCurrDisplayedScale } : undefined;
  };

  const balanceItem = (
    balance: IBalance,
    label: string,
    decimalPlaces: number,
    withZeros = false,
    isFirstChild = false
  ) => {
    const { available, frozen, currency } = balance;

    return (
      <>
        <BalanceItem
          label={label}
          value={available}
          decimalPlaces={decimalPlaces}
          withZeros={withZeros}
          currency={currency}
          isFirstChild={isFirstChild}
          amount={isFirstChild ? getCounterCurrencyAmount(available) : undefined}
        />
        {frozen !== 0 && (
          <BalanceItem
            label={t("dcaBots.balances.frozen")}
            value={frozen}
            decimalPlaces={decimalPlaces}
            withZeros={withZeros}
            currency={currency}
            tooltip={t("dcaBots.balances.frozenDesc")}
            amount={isFirstChild ? getCounterCurrencyAmount(frozen) : undefined}
          />
        )}
      </>
    );
  };

  const getSumsOfOpenInstantLimit = () => {
    let result = 0;

    botRunsList?.dcaBotRuns?.forEach((run) => {
      const { state, triggering, type } = run;

      if (state === DcaBotRunState.OPEN && triggering === OrderTriggering.INSTANT && type === OrderType.LIMIT) {
        result += 1;
      }
    });

    return result;
  };

  return (
    <Card
      sx={{ mb: 0 }}
      header={
        isBrokerage
          ? t("dcaBots.brokerage", { exchange: exchangeTitle ?? "" })
          : t("dcaBots.exchange", { exchange: exchangeTitle ?? "" })
      }
      subheader={
        exchangeEnum === ExchangeType.BYBITEU ? (
          <>
            {exchangeLabel && <Box component="span" sx={{ display: "block" }}>{exchangeLabel}</Box>}
            <Box component="span" sx={{ display: "block", color: "text.secondary", fontSize: "0.8rem" }}>
              {t("dcaBots.balances.bybitEuUnifiedTradingAccount")}
            </Box>
          </>
        ) : exchangeLabel ?? ""
      }
      bottomSection={
        <Stack alignItems="center" spacing={1}>
          <Stack alignItems="center" spacing={1} width={"fit-content"}>
            <Button
              disabled={!data || !isExchangeEnabled || !functional}
              onClick={() => setIsOpenModal(true)}
              startIcon={<AddchartIcon />}
              variant="contained">
              {t("dcaBots.balances.buySellNow", { baseCurrency })}
              {"..."}
            </Button>
            <Button
              onClick={() => setIsManualOrderModal(true)}
              startIcon={<CreateIcon />}
              sx={{ backgroundColor: "tertiary.main" }}
              color="info"
              fullWidth
              variant="contained">
              {t("dcaBots.balances.addManualTransaction")}
              {"..."}
            </Button>
            {canCreateExitStrategy && !hasExitStrategy && (
              <Button
                disabled={!data || !functional || (totalInvested ?? 0) <= 0}
                onClick={() => navigate(`/exit-strategie/${id}`)}
                startIcon={<CoinsSwap2 fontSize="inherit" />}
                color="error"
                fullWidth
                variant="contained">
                {t("dcaBots.balances.settingExitStrategy")}
                {"..."}
              </Button>
            )}
            <CreateGridStrategyButton id={id} currencyPair={`${baseCurrency}/${counterCurrency}`} />
          </Stack>
          {totalInvested === 0
            && (mode === DcaBotMode.MANUAL ? (
              <Alert severity="info">
                <Trans i18nKey="dcaBots.balances.manualModeInfoBox1" />
                <br/><br/>
                <Trans i18nKey="dcaBots.balances.manualModeInfoBox2" />
              </Alert>
            ) : (
              <Alert severity="info">
                {t("dcaBots.balances.infoBox1")}
                <br/><br/>
                {t("dcaBots.balances.infoBox2")}
              </Alert>
            ))}
        </Stack>
      }>
      <Grid container>
        <Grid item xs={12} sx={{ display: "flex", flexDirection: "column" }}>
          {data?.baseCurrencyBalance
            && balanceItem(
              data?.baseCurrencyBalance,
              isBrokerage
                ? t("dcaBots.balances.storedOnBrokerage", { exchange: exchangeTitle ?? "" })
                : t("dcaBots.balances.storedOnExchange", { exchange: exchangeTitle ?? "" }),
              baseCurrDisplayedScale,
              true,
              true
            )}
          {data?.counterCurrencyBalance
            && balanceItem(data?.counterCurrencyBalance, t("dcaBots.balances.available"), counterCurrDisplayedScale, true)}
          {isValidating && !data && (
            <CenterDiv>
              <CircularProgress color="primary" />
            </CenterDiv>
          )}
          {error
            && (error?.response?.status === 502 ? (
              <Alert severity="warning">
                <AlertTitle>
                  <Trans i18nKey="dcaBots.balances.warning.title" />
                </AlertTitle>
                <Trans i18nKey="dcaBots.balances.warning.description" />
              </Alert>
            ) : (
              <Alert severity="error">
                <AlertTitle>
                  <Trans
                    i18nKey={
                      isBrokerage ? "dcaBots.balances.errorBrokerageTitle" : "dcaBots.balances.errorExchangeTitle"
                    }
                  />
                </AlertTitle>
                <Trans i18nKey="dcaBots.balances.errorConnectionText" />
              </Alert>
            ))}
          {!functional && (
            <Alert severity="error">
              <AlertTitle>
                <Trans
                  i18nKey={
                    isBrokerage ? "exchangeConnections.functionalError.brokerage" : "exchangeConnections.functionalError.exchange"
                  }
                />
              </AlertTitle>
              <Trans i18nKey="exchangeConnections.functionalError.description" />
            </Alert>
          )}
          {isBybitEuBalanceEmpty && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <AlertTitle>
                <Trans i18nKey="dcaBots.balances.bybitEuEmptyBalanceWarning.title" />
              </AlertTitle>
              <Trans
                i18nKey="dcaBots.balances.bybitEuEmptyBalanceWarning.description"
                components={{
                  a: (
                    <Link
                      href="https://www.bybit.eu/cs-EU/user/assets/home/overview"
                      rel="noopener noreferrer"
                      target="_blank"
                    />
                  ),
                }}
              />
            </Alert>
          )}
        </Grid>
      </Grid>
      {isOpenModal && data && (
        <TradeModal
          isOpen={isOpenModal}
          id={id}
          handleClose={handleClose}
          handleInstantTrade={handleInstantTrade}
          baseCurrency={baseCurrency}
          counterCurrency={counterCurrency}
          exchangeEnum={exchangeEnum}
          balanceBase={data?.baseCurrencyBalance}
          balanceCounter={data?.counterCurrencyBalance}
          sumsOfOpenInstantLimit={getSumsOfOpenInstantLimit()}
        />
      )}
      {isManualOrderModal && exchangeEnum && (
        <ManualOrderModal
          id={id}
          handleClose={() => setIsManualOrderModal(false)}
          baseCurrency={baseCurrency}
          counterCurrency={counterCurrency}
          exchangeEnum={exchangeEnum}
          refreshData={mutateData}
        />
      )}
    </Card>
  );
};

export default Balance;
